import { useState } from 'react'

import { DeprecatedDateRangePicker, Flex, InputLabel } from '@procurify/ui'
import { type Moment } from 'moment'
import { type DateRangePickerShape } from 'react-dates'
import type ReactDates from 'react-dates'

import { type BudgetCategoryTypeOption } from '@webapp/components/Budgets/BudgetCategoryTypeSelect/BudgetCategoryTypeSelect'
import { useProcurifyIntl, useFeatureFlag } from '@webapp/hooks'
import { ExportTypes } from '@webapp/models/enums'
import { BudgetCategoryTypes } from '@webapp/models/enums/BudgetCategoryType'
import { Exporter } from '@webapp/platform/_unmigrated/Exporter'
import messages from './messages'
import { BudgetCategoryTypeSelect } from '../BudgetCategoryTypeSelect'

export interface BudgetCategoryExporterProps {
  handleModalClose: () => void
}

export type BudgetCategoryExportParamsType = {
  date_range_0: string | undefined
  date_range_1: string | undefined
  source_of_truth: string
  // include_spend: boolean
}

export const BudgetCategoryExporter = ({
  handleModalClose,
}: BudgetCategoryExporterProps) => {
  const intl = useProcurifyIntl()
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const [dateValues, setDateValues] = useState<{
    focusedInput: DateRangePickerShape['focusedInput']
    endDate: Moment
    startDate: Moment
  }>({
    focusedInput: null,
    endDate: null,
    startDate: null,
  })

  const [exportParams, setExportParams] =
    useState<BudgetCategoryExportParamsType>({
      date_range_0: '',
      date_range_1: '',
      source_of_truth: '',
      // include_spend: false,
    })

  /* const onIncludeSpendClick = () => {
    setExportParams({
      ...exportParams,
      include_spend: !exportParams.include_spend,
    })
  }*/

  const onDateRangeChange = ({
    endDate: newEndDate,
    startDate: newStartDate,
  }: {
    endDate: Moment
    startDate: Moment
  }) => {
    setDateValues({
      ...dateValues,
      endDate: newEndDate,
      startDate: newStartDate,
    })

    const dateRangeStart = newStartDate
      ? newStartDate.format('YYYY-MM-DD')
      : null
    const dateRangeEnd = newEndDate ? newEndDate.format('YYYY-MM-DD') : null
    if (dateRangeStart && dateRangeEnd) {
      setExportParams({
        ...exportParams,
        date_range_1: dateRangeEnd,
        date_range_0: dateRangeStart,
      })
    }
  }

  const onDateRangeFocusChange = (
    focusedInput: ReactDates.FocusedInputShape
  ) => {
    setDateValues((state) => ({
      ...state,
      focusedInput,
    }))

    if (focusedInput === null) {
      setDateValues((state) => {
        if (!state.startDate || !state.endDate) {
          return {
            startDate: null,
            endDate: null,
            focusedInput,
          }
        }
        return {
          startDate: state.startDate,
          endDate: state.endDate,
          focusedInput,
        }
      })
    }
  }

  const onBudgetCategoryTypeChange = (option: BudgetCategoryTypeOption) => {
    setExportParams({
      ...exportParams,
      source_of_truth: option.value,
    })
  }

  return (
    <Exporter
      type={ExportTypes.SPEND_CATEGORIES}
      baseEndpoint='/api/v3/budget-categories/'
      exportParams={exportParams}
      exportSampleParams={exportParams}
      onCancel={handleModalClose}
      exportStatusUrl='/#/settings/exportcsv/requested/spend_categories'
      renderExportFormFilters={
        <Flex.Row>
          <Flex.Item>
            <InputLabel>
              {intl.formatMessage(messages.budgetDateRangeLabel)}
            </InputLabel>
            <DeprecatedDateRangePicker
              startDateId='dateRangeStart'
              endDateId='dateRangeEnd'
              startDate={dateValues.startDate}
              endDate={dateValues.endDate}
              focusedInput={dateValues.focusedInput}
              onDatesChange={onDateRangeChange}
              onFocusChange={onDateRangeFocusChange}
            />
          </Flex.Item>
          <Flex.Item>
            <InputLabel>
              {intl.formatMessage(
                FT_5817_ENABLE_BUDGETS_REDESIGN
                  ? messages.budgetsToExportLabel
                  : messages.budgetCategoriesToExportLabel
              )}
            </InputLabel>
            <BudgetCategoryTypeSelect
              onChange={(option) => onBudgetCategoryTypeChange(option)}
              defaultParam={BudgetCategoryTypes.ALL}
            />
          </Flex.Item>
          {/* <Flex.Item>
            <InputLabel>
              {intl.formatMessage(messages.includeSpendToggleLabel)}
            </InputLabel>
            <Toggle
              aria-label={intl.formatMessage(messages.includeSpendToggleLabel)}
              checked={exportParams.include_spend}
              onClick={onIncludeSpendClick}
            />
      </Flex.Item>*/}
        </Flex.Row>
      }
    />
  )
}
